import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * useWhisperRecorder
 * ------------------
 * Cross-platform speech-to-text via MediaRecorder + backend Whisper-1.
 * Works on iOS Safari (m4a/mp4), Android Chrome (webm/opus) and desktop.
 *
 * Returns:
 *   recording   – true while microphone is active
 *   transcribing – true while waiting for backend transcription
 *   start()      – request mic + start recording
 *   stop()       – stop recording and trigger transcription
 *   cancel()     – abort without transcribing
 *
 * onTranscript(text) is called once when Whisper returns the text.
 */
const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",       // iOS Safari
    "audio/aac",
    "",                // browser default fallback
  ];
  for (const m of candidates) {
    if (m === "" || MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
};

export function useWhisperRecorder({ onTranscript, language = "de", onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");
  const streamRef = useRef(null);
  const cancelledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => () => {
    try { mediaRef.current?.state === "recording" && mediaRef.current.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (recording || transcribing) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      onError?.("Mikrofon-API nicht verfügbar in diesem Browser");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      onError?.(err?.name === "NotAllowedError"
        ? "Mikrofonzugriff verweigert – bitte in den Browser-Einstellungen erlauben."
        : "Mikrofon konnte nicht aktiviert werden");
      return;
    }
    streamRef.current = stream;

    const mime = pickMimeType();
    if (mime === null) {
      stream.getTracks().forEach((t) => t.stop());
      onError?.("Audio-Aufnahme wird in diesem Browser nicht unterstützt");
      return;
    }
    mimeRef.current = mime;

    let mr;
    try {
      mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      mr = new MediaRecorder(stream);
    }
    chunksRef.current = [];
    cancelledRef.current = false;

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      const tracks = streamRef.current?.getTracks() || [];
      tracks.forEach((t) => t.stop());
      streamRef.current = null;
      setRecording(false);

      if (cancelledRef.current) {
        chunksRef.current = [];
        return;
      }
      if (chunksRef.current.length === 0) return;
      const blobMime = mr.mimeType || mime || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: blobMime });
      chunksRef.current = [];

      // Pick filename extension that Whisper recognises
      let ext = "webm";
      if (blobMime.includes("mp4") || blobMime.includes("aac")) ext = "m4a";
      else if (blobMime.includes("ogg")) ext = "ogg";
      else if (blobMime.includes("wav")) ext = "wav";

      setTranscribing(true);
      let cleared = false;
      try {
        const fd = new FormData();
        fd.append("file", blob, `recording.${ext}`);
        fd.append("language", language);
        const r = await api.post("/ai/transcribe", fd, {
          // IMPORTANT: do NOT manually set Content-Type. Axios + the browser
          // need to auto-add the multipart boundary; setting it by hand
          // strips the boundary and the server returns 422.
          timeout: 60000,
          // Opt this request out of the global 401 → /login redirect.
          // A failing transcribe MUST NOT take the whole app down – it just
          // surfaces a toast via onError below. The redirect was the most
          // likely root cause of the "black screen after dictation" bug
          // reported on mobile.
          skip401Redirect: true,
        });
        const text = String(r.data?.text || "").trim();
        if (text) {
          try {
            onTranscript?.(text);
          } catch (consumerErr) {
            // A bug in the consumer (e.g. setState on stale ref) must not
            // bubble up and tear down the React tree. We log + toast it.
            // eslint-disable-next-line no-console
            console.error("[Novatus] onTranscript handler crashed:", consumerErr);
            onError?.("Transkript konnte nicht eingefügt werden");
          }
        } else {
          onError?.("Keine Sprache erkannt – bitte erneut versuchen");
        }
      } catch (e) {
        // Handle every shape of axios error without crashing the caller
        let msg = "Transkription fehlgeschlagen";
        try {
          const d = e?.response?.data?.detail;
          if (typeof d === "string" && d) msg = d;
          else if (e?.message) msg = String(e.message);
        } catch { /* noop */ }
        if (e?.response?.status === 401) {
          msg = "Sitzung abgelaufen – bitte neu anmelden, dann erneut diktieren.";
        }
        onError?.(msg);
      } finally {
        cleared = true;
        setTranscribing(false);
      }
      // Defensive: even if setTranscribing somehow throws (unmounted component
      // race), force a microtask so we don't leave the UI thinking we're busy.
      if (!cleared) setTranscribing(false);
    };

    try {
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      onError?.("Aufnahme konnte nicht gestartet werden");
    }
  };

  const stop = () => {
    const mr = mediaRef.current;
    if (mr && mr.state === "recording") {
      try { mr.stop(); } catch { /* noop */ }
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
    stop();
  };

  // Available if browser supports MediaRecorder + getUserMedia
  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  return { recording, transcribing, start, stop, cancel, supported };
}
